import { DEFAULT_ROUTINE_TIME_BOUNDARIES } from 'assets/data';
import CheckBox from 'components/common/CheckBox';
import { CHECKBOX, CUSTOM_ROUTINE_TRIGGER } from 'enums';
import { CUSTOM_ROUTINE_FEATURE } from 'constants/general';
import { useCallback, useContext, useMemo } from 'react';
import { t } from 'services/i18n.service';
import { useAppDispatch } from 'store/hooks';
import type { AppDispatch } from 'store';
import { updateCustomRoutine } from 'store/reducer/setting/slice';
import { getTimeWheelPickerInitialValue } from 'utils/helpers';
import { getRoutineTriggerOptions } from 'utils/settings.util';
import { CustomRoutineContext } from '..';

const dispatchRoutineTimeIfChanged = (
  dispatch: AppDispatch,
  position: number,
  feature: (typeof CUSTOM_ROUTINE_FEATURE)[keyof typeof CUSTOM_ROUTINE_FEATURE],
  currentTime: string | undefined,
  defaultTime: string
) => {
  const nextTime = getTimeWheelPickerInitialValue(currentTime, defaultTime);
  if (nextTime === (currentTime ?? '')) return;

  dispatch(updateCustomRoutine({ feature, position, value: nextTime }));
};

const RoutineTrigger = () => {
  const dispatch = useAppDispatch();
  const customRoutine = useContext(CustomRoutineContext);

  const [ON_DEMAND, ON_SCHEDULE] = useMemo(
    () => getRoutineTriggerOptions(customRoutine?.name ?? 'custom_routine'),
    [customRoutine?.name]
  );

  const handleRoutineTrigger = useCallback(
    (value: CUSTOM_ROUTINE_TRIGGER, checked: boolean) => {
      if (!customRoutine || customRoutine.trigger === value) return;

      const nextTrigger =
        checked && value === CUSTOM_ROUTINE_TRIGGER.ON_DEMAND
          ? CUSTOM_ROUTINE_TRIGGER.ON_DEMAND
          : CUSTOM_ROUTINE_TRIGGER.ON_SCHEDULE;

      dispatch(
        updateCustomRoutine({
          feature: CUSTOM_ROUTINE_FEATURE.TRIGGER,
          position: customRoutine.position,
          value: nextTrigger
        })
      );

      if (nextTrigger !== CUSTOM_ROUTINE_TRIGGER.ON_SCHEDULE) return;

      dispatchRoutineTimeIfChanged(
        dispatch,
        customRoutine.position,
        CUSTOM_ROUTINE_FEATURE.START_TIME,
        customRoutine.start_time,
        DEFAULT_ROUTINE_TIME_BOUNDARIES.START_TIME
      );
      dispatchRoutineTimeIfChanged(
        dispatch,
        customRoutine.position,
        CUSTOM_ROUTINE_FEATURE.END_TIME,
        customRoutine.end_time,
        DEFAULT_ROUTINE_TIME_BOUNDARIES.END_TIME
      );
    },
    [customRoutine, dispatch]
  );

  return (
    <div
      className='flex-1 flex items-center justify-center ~px-4/8 ~py-4/5 relative rounded-md shadow bg-primary-100 dark:bg-secondary-dark-300 dark:border dark:border-secondary-dark-500'
      data-testid='routine-trigger-options'>
      <p className='absolute -top-2 left-3 px-2 pb-0.5 rounded text-[0.7rem] bg-primary-50 dark:bg-secondary-dark-300 text-black dark:text-secondary-dark-900'>
        {t('trigger')}
      </p>
      <div className='w-max flex items-start sm:items-center ~gap-5/10 flex-col sm:flex-row'>
        <CheckBox
          label={ON_DEMAND.label}
          checked={customRoutine?.trigger === CUSTOM_ROUTINE_TRIGGER.ON_DEMAND}
          type={CHECKBOX.RADIO}
          tooltipMessage={ON_DEMAND.tooltip}
          inputStyles='accent-black dark:accent-secondary-dark-500 outline-none ~w-4/5 ~h-4/5 cursor-pointer'
          labelStyles='min-w-max ~text-xs/sm flex items-center gap-2 cursor-pointer text-black dark:text-secondary-dark-900'
          onChange={({ target: { checked } }) =>
            handleRoutineTrigger(ON_DEMAND.value, checked)
          }
        />
        <CheckBox
          label={ON_SCHEDULE.label}
          checked={
            customRoutine?.trigger === CUSTOM_ROUTINE_TRIGGER.ON_SCHEDULE
          }
          type={CHECKBOX.RADIO}
          tooltipMessage={ON_SCHEDULE.tooltip}
          inputStyles='accent-black dark:accent-secondary-dark-500 outline-none ~w-4/5 ~h-4/5 cursor-pointer'
          labelStyles='min-w-max ~text-xs/sm flex items-center gap-2 cursor-pointer text-black dark:text-secondary-dark-900'
          onChange={({ target: { checked } }) =>
            handleRoutineTrigger(ON_SCHEDULE.value, checked)
          }
        />
      </div>
    </div>
  );
};

export default RoutineTrigger;
